import React, { useCallback, useContext, useEffect, useRef, ReactNode, RefObject } from 'react'
import { View, findNodeHandle, ScrollView, ViewProps } from 'react-native'
import { isWeb } from 'APP/Helpers/checkPlatform'

interface ScrollIntoViewOptions {
  align?: 'top' | 'center'
  insets?: {
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
}

interface ScrollViewProviderProps {
  children: ReactNode
  scrollViewRef: RefObject<ScrollView>
}

interface ScrollableItemWrapperProps extends ViewProps {
  onUnmount: () => void
}

const ScrollViewContext = React.createContext<RefObject<ScrollView> | null>(null)

export const ScrollViewProvider = ({ children, scrollViewRef }: ScrollViewProviderProps) => (
  <ScrollViewContext.Provider value={scrollViewRef}>{children}</ScrollViewContext.Provider>
)

/**
 * Helper View wrapper to access ref and unmount cycle
 */
export const ScrollableItemWrapper = React.forwardRef<View, ScrollableItemWrapperProps>(
  ({ onUnmount, ...props }, ref) => {
    useEffect(() => onUnmount, [])
    return <View ref={ref} {...props} />
  }
)
ScrollableItemWrapper.displayName = 'ScrollableItemWrapper'

export interface ScrollableItemProps {
  ref: (el: View | null) => void
  onUnmount: () => void
}

export interface UseDynamicScrollIntoViewReturn {
  getItemProps: (id: string) => ScrollableItemProps
  scrollIntoView: (id: string, options?: ScrollIntoViewOptions) => void
}

/**
 * For managing a bunch of views that change over time
 * Uses native ScrollView ref and measureLayout for scrolling
 */
export function useDynamicScrollIntoView(
  defaultOptions?: ScrollIntoViewOptions
): UseDynamicScrollIntoViewReturn {
  const elements = useRef<Record<string, View | null>>({})
  const scrollViewRef = useContext(ScrollViewContext)

  const safeFindNodeHandle = useCallback((node: View | ScrollView | null) => {
    if (!node || isWeb()) {
      return null
    }

    return findNodeHandle(node)
  }, [])

  const getOnUnmount = (id: string) => () => {
    delete elements.current[id]
  }
  const getItemRef = (id: string) => (el: View | null) => {
    elements.current[id] = el
  }
  const scrollIntoView = useCallback(
    (id: string, options = defaultOptions) => {
      const element = elements.current[id]
      const scrollView = scrollViewRef?.current

      if (!element || !scrollView) {
        return
      }
      const scrollViewHandle = safeFindNodeHandle(scrollView)
      const elementHandle = safeFindNodeHandle(element)

      if (!scrollViewHandle || !elementHandle) {
        return
      }

      element.measureLayout(
        scrollViewHandle,
        (_x: number, y: number, _width: number, height: number) => {
          const { align = 'top', insets = {} } = options || {}
          const topInset = insets.top || 0

          let scrollToY = y - topInset

          if (align === 'center') {
            ;(scrollView as unknown as View).measure(
              (_scrollX: number, _scrollY: number, _scrollWidth: number, scrollHeight: number) => {
                scrollToY = y - scrollHeight / 2 + height / 2 - topInset
                scrollView.scrollTo({ y: Math.max(0, scrollToY), animated: true })
              }
            )
          } else {
            scrollView.scrollTo({ y: Math.max(0, scrollToY), animated: true })
          }
        },
        () => {}
      )
    },
    [defaultOptions, scrollViewRef]
  )

  const getItemProps = useCallback(
    (id: string) => ({
      ref: getItemRef(id),
      onUnmount: getOnUnmount(id),
    }),
    []
  )

  return { getItemProps, scrollIntoView }
}
